import type { TrackEventOptions, TrackPageOptions } from '@cego/tracking'
import { createSharedComposable } from '@vueuse/core'
import { onNuxtReady } from '#app'
import { useCoreConfig, useCoreLogger, useRouter, useVisitorIdCookie } from '#imports'

type QueuedCall
    = | { type: 'event', payload: TrackEventOptions }
        | { type: 'page', payload: TrackPageOptions }

export const useTracking = createSharedComposable(() => {
    if (import.meta.server) {
        return {
            trackEvent: async (_payload: TrackEventOptions) => {},
            trackPageRequest: async (_payload: TrackPageOptions) => {},
        }
    }

    const { trackingService, site } = useCoreConfig()

    // Constructing TrackingManual starts a screen-fingerprint timer (reads the `screen` global
    // on an interval), so only boot the SDK when tracking is actually enabled.
    if (!trackingService?.enabled) {
        return {
            trackEvent: async (_payload: TrackEventOptions) => {},
            trackPageRequest: async (_payload: TrackPageOptions) => {},
        }
    }

    const { setVisitorIdCookie } = useVisitorIdCookie()
    const logger = useCoreLogger()
    const router = useRouter()

    // Stamped at emit time so queued events keep the route they were fired from, not the route at flush.
    function withRoutePath(payload: TrackEventOptions): TrackEventOptions {
        return {
            ...payload,
            meta_data: [...(payload.meta_data ?? []), { route_path: router.currentRoute.value.path }],
        }
    }

    const queue: QueuedCall[] = []
    let TrackingManual: typeof import('@cego/tracking').TrackingManual | null = null

    async function processEvent(payload: TrackEventOptions) {
        setVisitorIdCookie()
        const { event_type = '', event_name = '', meta_data = [], log_entry = false } = payload
        await TrackingManual!.trackEvent({
            event_name,
            event_type,
            meta_data,
            ...(log_entry && { log_entry }),
        })
    }

    async function processPage(payload: TrackPageOptions) {
        setVisitorIdCookie()
        const { url = '', referer = '' } = payload
        await TrackingManual!.trackPage({
            url,
            referer,
        })
    }

    async function flush() {
        while (queue.length > 0) {
            const item = queue.shift()!
            try {
                if (item.type === 'event') {
                    await processEvent(item.payload)
                }
                else {
                    await processPage(item.payload)
                }
            }
            catch {
                // A queued call already resolved to its caller, so only fixed text is logged here:
                // both the error and the payload can carry values tracking must not write out.
                logger.error('A queued tracking call failed')
            }
        }
    }

    onNuxtReady(async () => {
        const mod = await import('@cego/tracking')
        TrackingManual = mod.TrackingManual
        new TrackingManual({ brand: site.brand })
        await flush()
    })

    return {
        trackEvent: async (payload: TrackEventOptions) => {
            const enrichedPayload = withRoutePath(payload)
            if (!TrackingManual) {
                queue.push({ type: 'event', payload: enrichedPayload })
                return
            }
            await processEvent(enrichedPayload)
        },
        trackPageRequest: async (payload: TrackPageOptions) => {
            if (!TrackingManual) {
                queue.push({ type: 'page', payload })
                return
            }
            await processPage(payload)
        },
    }
})
